<template>
    <div class="telekom-angebote">
        <BasicBreadcrumb
            :items="[
                { label: 'Telekom Angebote', to: '' }
            ]"
        />
        <div :key="currentStore.id" class="lg:container mx-auto p-8 lg:px-0">
            <ContentHeroBanner
                v-for="banner in heroBanner?.items" :key="banner?.id"
                :img-mobile="banner?.imageUrlMobile"
                :img-desktop="banner?.imageUrl"
                :title="banner?.title"
                :background-img-mobile="backgroundImgMobile"
                :background-img-desktop="backgroundImgDesktop"
            />
            <div v-if="hasTariffs">
                <BasicBtErrorBoundary>
                    <TariffOfferSlider
                        :key="Date.now()"
                        :tenant-id="tenantId"
                        :show-in-carousel="true"
                        :title="'Unsere aktuellen Top-Angebote der Telekom - vor Ort im Markt für Dich verfügbar'"
                        :store="currentStore"
                        :link-path="'/shop/telekom-tarifangebote'"
                    />
                </BasicBtErrorBoundary>
            </div>
            <div v-if="isVisibleWMVideo">
                <div style="position: relative; padding-top: 56.25%;" class="my-8">
                    <iframe
                        title="Alle Spiele nur bei MagentaTV"
                        aria-label="Alle Spiele nur bei MagentaTV"
                        src="https://customer-tlje1rwg5ehqb0q0.cloudflarestream.com/138b91b91562dd7d6e04e26003b07ad2/iframe?poster=https%3A%2F%2Fcustomer-tlje1rwg5ehqb0q0.cloudflarestream.com%2F138b91b91562dd7d6e04e26003b07ad2%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
                        loading="lazy"
                        style="border: none; position: absolute; top: 0; left: 0; height: 100%; width: 100%;"
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                        allowfullscreen="true"
                        class="rounded-3xl"
                    />
                </div>
            </div>
            <div class="pt-4 pb-12 container mx-auto font-bold text-paragraph text-2xl text-center">
                Alle Informationen und Angebote zu den Themen Mobilfunk, MagentaTV,<br>
                Internet, Glasfaser und Kombipakete findest du hier
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 pb-8">
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconContract"
                    url="/telekom-angebote/mobilfunk"
                    :title="'Handy mit Vertrag'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconMagenta"
                    url="/telekom-angebote/magentatv"
                    :title="'MagentaTV'"
                />
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-8 pb-8">
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconInternet"
                    url="/telekom-angebote/internet"
                    :title="'Internet'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconOptical"
                    url="/telekom-angebote/glasfaser"
                    :title="'Glasfaser'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconCombi"
                    url="/telekom-angebote/kombipakete"
                    :title="'Kombi-Pakete'"
                />
            </div>
        </div>
        <TariffEyeCatcher />
    </div>
</template>

<script setup lang="ts">

    import { hasTenantTariffs } from "~/composables/tariffs";
    import type { ContentBlock } from '~~/models/Tariff/ContentBlock';
    import { ContentBlockType } from '~~/models/Tariff/ContentBlock';

    import backgroundImgMobile from '~/assets/img/tariff-topic/telekom/ssb_BG_mobile.png';
    import backgroundImgDesktop from '~/assets/img/tariff-topic/telekom/ssb_BG.png';
    import backgroundTile from '~/assets/img/tariff-topic/telekom/bg_icons.png';
    import iconContract from '~/assets/img/tariff-topic/telekom/two/mobilfunk.png';
    import iconMagenta from '~/assets/img/tariff-topic/telekom/two/magentaTV.png';
    import iconInternet from '~/assets/img/tariff-topic/telekom/three/internet.png';
    import iconOptical from '~/assets/img/tariff-topic/telekom/three/glasfaser.png';
    import iconCombi from '~/assets/img/tariff-topic/telekom/three/kombi-pakete.png';

    const tenantId: string = "692ef56ad1b19b630ac62ff3";
    const currentStore = useCurrentStore();
    const hasTariffs = ref<boolean>();

    const storeName = currentStore?.value?.name ?? 'expert';
    const pageTitle = 'Telekom Angebote bei ' + storeName + ' | Tarife, Smartphones & Internet bei expert';
    const pageDescription = 'Telekom Angebote bei ' + storeName + ': Entdecke Mobilfunk, Internet & MagentaTV Tarife bei expert – inklusive Cashback, top Smartphones & attraktiven Deals. Jetzt informieren!';
    useHead({
        title: pageTitle,
        meta: [{ name: 'description', content: pageDescription }
        ]
    });    

    const isVisibleWMVideo = new Date() <= new Date('2026-05-31');

    const { data: heroBanner } = await useFetch<{ items: ContentBlock[] }>(
        `/api/nea/tariff-service/contentBlock`,
        {
            query: {
                page: 0,
                validOnly: true,
                pageSize: 1,
                type: ContentBlockType.TELEKOM_BANNER
            }
        }
    );

    const checkHasTariffs = async (storeId: string): Promise<void> => {
        hasTariffs.value = await hasTenantTariffs(tenantId, storeId, {showInCarousel: true});
    };

    await checkHasTariffs(currentStore.value?.id);
    await checkStoreHasPage(tenantId);

    watch(currentStore, async (store) => {
        const storeId: string = store?.id;
        if (!storeId) {
            return;
        }
        await checkHasTariffs(storeId);
        await checkStoreHasPage(tenantId);
    });
</script>